/**
 * Section Renderer v2.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * THE ONE SOLUTION for section re-rendering.
 *
 * Re-renders Shopify sections using the Section Rendering API with:
 * - Surgical DOM morphing via Alpine.morph() (preserves inputs, focus, Alpine state)
 * - Caching (avoids refetching same section)
 * - Request deduplication (multiple calls share one fetch)
 * - Abort controllers (cancel pending requests)
 *
 * MENTAL MODEL:
 * ─────────────────────────────────────────────────────────────────────────────
 * This is the ONLY way to morph sections in this theme. All code paths use this.
 *
 * ES Module access:
 *   import { morphSection, sectionRenderer } from '@theme/section-renderer';
 *   morphSection('cart-drawer', html);
 *
 * IIFE/Global access (for alpine-stores.js):
 *   window.Theme.morphSection('cart-drawer', html);
 *   window.Theme.morphSections({ 'cart-drawer': html });
 *
 * Why Alpine.morph():
 * - Preserves input values (user was typing)
 * - Preserves focus (accessibility)
 * - Preserves Alpine state (x-data, stores)
 * - Preserves open/close state (details, dialog elements)
 * - Preserves checked state (checkboxes, radios)
 * - Minimal DOM changes (better performance)
 * - One less file to maintain (uses Alpine plugin instead of custom morph.js)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

// Ensure window.Theme exists
window.Theme = window.Theme || {};

// Get Logger from window.Theme
const Logger = window.Theme?.Logger || {
  ns: () => ({
    debug: () => {},
    info: () => {},
    warn: console.warn.bind(console),
    error: console.error.bind(console)
  })
};

const log = Logger.ns('section-renderer');

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

    if (!abortController.signal.aborted) {
      this.#abortControllersBySectionId.delete(sectionId);
      morphSection(sectionId, sectionHTML);
    }

    return sectionHTML;
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }

    pendingPromise = fetch(sectionUrl).then((response) => {
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    const sectionHTML = await pendingPromise;
    this.#pendingPromises.delete(sectionUrl);

    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }

  /**
   * Clears the section cache
   */
  clearCache() {
    this.#cache.clear();
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * Morphs the existing section element with the new section contents
 * Uses Alpine.morph() for surgical DOM updates that preserve state.
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    log.error(`Section ${sectionId} not found`);
    return;
  }

  if (!newElement) {
    log.error(`Section ${sectionId} not found in response`);
    return;
  }

  // Use Alpine.morph() with hooks for web component support
  if (typeof Alpine !== 'undefined' && Alpine.morph) {
    log.debug('Morphing section', { sectionId });

    Alpine.morph(existingElement, newElement, {
      updating(el, toEl, childrenOnly, skip) {
        // Skip Shopify Theme Editor comment nodes
        if (toEl.nodeType === Node.COMMENT_NODE &&
            toEl.nodeValue === 'shopify:rendered_by_section_api') {
          skip();
        }

        // Preserve data-open attribute on drawers (dynamic state, not in SSR template)
        if (el instanceof HTMLElement && el.hasAttribute('data-open')) {
          toEl.setAttribute('data-open', '');
        }
      },
      updated(el, toEl) {
        // Call updatedCallback on web components (Component base class uses this)
        if (el instanceof HTMLElement && typeof el.updatedCallback === 'function') {
          queueMicrotask(() => el.updatedCallback());
        }
      },
      key(el) {
        // Use id attribute for element matching
        return el.id;
      }
    });
  } else {
    // Fallback: simple innerHTML replacement (Alpine not loaded yet - rare)
    log.warn('Alpine.morph not available, using innerHTML fallback');
    existingElement.innerHTML = newElement.innerHTML;
  }
}

/**
 * Morph multiple sections from API response
 * @param {Object} sectionsHtml - { sectionId: html, ... }
 */
export function morphSections(sectionsHtml) {
  if (!sectionsHtml || typeof sectionsHtml !== 'object') return;
  Object.entries(sectionsHtml).forEach(([id, html]) => morphSection(id, html));
}

export const sectionRenderer = new SectionRenderer();

// ═══════════════════════════════════════════════════════════════════════════════
// GLOBAL EXPOSURE (for IIFE access from alpine-stores.js)
// ═══════════════════════════════════════════════════════════════════════════════

window.Theme.morphSection = morphSection;
window.Theme.morphSections = morphSections;
window.Theme.sectionRenderer = sectionRenderer;
